import { CalendarModel } from '@bryntum/schedulerpro';
import moment, { Moment } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { Allocation, ResourceType } from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { isDateBetweenRange } from 'components/organisms/resource-management/utils/dates';
import {
  resourceIdForProjectAsLeaf,
  resourceIdForTeamAsLeaf,
  resourceIdForUserAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { numberOfWorkingDaysBetweenDates } from 'components/organisms/timesheets/common/utils';
import { floatUid } from 'utils/uid';
import { unreachable } from 'utils/unreachable';
import {
  AllocationEvent,
  EventModelConfigWithoutMandatoryFields,
  SchedulerEventType,
} from '../../event-types';
import { ExcludeNonWorkingDaysParam, GroupBy } from '../../types';

export const PHASE_STYLE = {
  defaultTop: 8,
  defaultHeight: 24,
  totalAvailableSpace: 32,
};

export const getClassNameBasedOnAllocatedPercentage = (
  allocatedPercentage: number,
  classToAdd = '',
): string => {
  if (allocatedPercentage > 100) {
    return `over-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 100 && allocatedPercentage > 80) {
    return `mostly-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 80 && allocatedPercentage > 50) {
    return `moderately-allocated ${classToAdd}`;
  }
  if (allocatedPercentage <= 50 && allocatedPercentage > 30) {
    return `mildly-allocated ${classToAdd}`;
  }

  return `sparsely-allocated ${classToAdd}`;
};

export const getSchedulerEventsClass = (
  type: SchedulerEventType,
  classToAppend = '',
): string => {
  switch (type) {
    case SchedulerEventType.Allocation:
      return `allocation ${classToAppend}`;
    case SchedulerEventType.UserAllocationSummary:
      return `user_allocation_summary ${classToAppend}`;
    case SchedulerEventType.TeamAllocationSummary:
      return `team_allocation_summary ${classToAppend}`;
    case SchedulerEventType.CompanyAllocationSummary:
      return `company_allocation_summary ${classToAppend}`;
    case SchedulerEventType.ProjectTimeline:
      return `project-timeline ${classToAppend}`;
    case SchedulerEventType.ProjectAsALeafTimeline:
      return `project_as_a_leaf_timeline ${classToAppend}`;
    case SchedulerEventType.ProjectPhase:
      return `project-phase ${classToAppend}`;
    case SchedulerEventType.DemandRole:
      return `demand_role ${classToAppend}`;
    case SchedulerEventType.DemandCapacity:
      return `demand_capacity ${classToAppend}`;
    case SchedulerEventType.TotalAllocation:
      return `total_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalAllocation:
      return `user_total_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalSoftAllocation:
      return `user_total_soft_allocation ${classToAppend}`;
    case SchedulerEventType.UserTotalHardAllocation:
      return `user_total_hard_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalAllocation:
      return `team_total_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalSoftAllocation:
      return `team_total_soft_allocation ${classToAppend}`;
    case SchedulerEventType.TeamTotalHardAllocation:
      return `team_total_hard_allocation ${classToAppend}`;
    case SchedulerEventType.UserTimeOff:
      return `time-off-event ${classToAppend}`;
    case SchedulerEventType.UserFte:
      return `user_fte ${classToAppend}`;
    case SchedulerEventType.PartnerFte:
      return `partner_fte ${classToAppend}`;
    case SchedulerEventType.TimeOffFte:
      return `timeoff_fte ${classToAppend}`;
    case SchedulerEventType.HolidayFte:
      return `holiday_fte ${classToAppend}`;
    case SchedulerEventType.CapacityAllocationDiffFte:
      return `capacity_allocation_diff_fte ${classToAppend}`;
    case SchedulerEventType.ProjectsAllocationInRoleFte:
      return `projects_allocation_fte ${classToAppend}`;
    case SchedulerEventType.Holiday:
      return `holiday-event ${classToAppend}`;
    default:
      return unreachable(type);
  }
};

const ReadOnlyEventProps = {
  calendar: '24h' as unknown as CalendarModel,
  resizable: false,
  draggable: false,
};

export const getSchedulerEventProps = (
  type: SchedulerEventType,
  defaultValues: Partial<EventModelConfigWithoutMandatoryFields> = {},
): Partial<EventModelConfigWithoutMandatoryFields> => {
  switch (type) {
    case SchedulerEventType.Allocation:
      return {
        eventColor: 'var(--blue-20)',
        cls: getSchedulerEventsClass(type),
        calendar: '24h' as unknown as CalendarModel,
        stickyContents: false,
        ...defaultValues,
      };
    case SchedulerEventType.UserAllocationSummary:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,

        ...defaultValues,
      };
    case SchedulerEventType.TeamAllocationSummary:
    case SchedulerEventType.CompanyAllocationSummary:
      return {
        eventColor: 'var(--yellow-30)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectTimeline:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectAsALeafTimeline:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTimeOff:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.Holiday:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--blue-60)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectPhase:
      return {
        cls: getSchedulerEventsClass(type),
        eventColor: 'var(--green-20)',
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.DemandRole:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.DemandCapacity:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalSoftAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserTotalHardAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalSoftAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TeamTotalHardAllocation:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.UserFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.PartnerFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.TimeOffFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.HolidayFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.CapacityAllocationDiffFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    case SchedulerEventType.ProjectsAllocationInRoleFte:
      return {
        eventColor: 'var(--green-20)',
        cls: getSchedulerEventsClass(type),
        ...ReadOnlyEventProps,
        ...defaultValues,
      };
    default:
      return unreachable(type);
  }
};
interface OverlappingParam {
  startDate: Moment;
  endDate: Moment;
}
export const isOverlapping = (
  { startDate: startDateA, endDate: endDateA }: OverlappingParam,
  { startDate: startDateB, endDate: endDateB }: OverlappingParam,
): boolean =>
  isDateBetweenRange(startDateA, {
    startDate: startDateB,
    endDate: endDateB,
  }) ||
  isDateBetweenRange(endDateA, {
    startDate: startDateB,
    endDate: endDateB,
  }) ||
  isDateBetweenRange(startDateB, {
    startDate: startDateA,
    endDate: endDateA,
  }) ||
  isDateBetweenRange(endDateB, {
    startDate: startDateA,
    endDate: endDateA,
  });

export const asAllocationPayload = (
  event: AllocationEventModel,
): Allocation => ({
  id: Number(event.id),
  ...(event.get('userId') != null
    ? {
        type: ResourceType.User,
        userId: event.get('userId'),
        ...(event.get('referenceTeamId')
          ? {
              roleId: event.get('roleId'),
              referenceTeamId: event.get('referenceTeamId'),
            }
          : {}),
        overAllocations: event.get('overAllocations'),
      }
    : {
        type: ResourceType.Team,
        teamId: event.get('teamId'),
        roleId: event.get('roleId'),
      }),
  startDate: moment(event.startDate).format(DEFAULT_DATE_FORMAT),
  endDate: moment(event.endDate).format(DEFAULT_DATE_FORMAT),
  minutes: event.get('minutes'),
  totalMinutes: event.get('totalMinutes'),
  projectPhaseId: event.get('projectPhase')?.id,
  billable: event.get('billable') ?? false,
  projectId: event.get('projectId'),
  medium: event.get('medium'),
  sources: event.get('sources'),
});

// :TODO Have a separate type for this function
export type NewAllocationParam = Allocation & {
  projectPhase?: {
    id: number;
    name: string;
  };
};

export const idForNewAllocation = (): number => -1 * floatUid();
export const newAllocationEvent = (
  groupBy: GroupBy,
  allocationParam: NewAllocationParam,
  exclusionParams: ExcludeNonWorkingDaysParam,
  softAllocationsEnabled?: boolean,
): AllocationEvent => {
  const startDateMoment = moment(allocationParam.startDate).startOf('day');
  const endDateMoment = moment(allocationParam.endDate).endOf('day');

  const numberOfWorkingDays = numberOfWorkingDaysBetweenDates(
    startDateMoment,
    endDateMoment,
    exclusionParams.workingDaysConfig,
  );
  const commonProps = {
    type: SchedulerEventType.Allocation as const,
    id: allocationParam.id,
    startDate: startDateMoment.toDate(),
    endDate: endDateMoment.toDate(),
    minutes: allocationParam.minutes,
    totalMinutes: numberOfWorkingDays * allocationParam.minutes,
    projectPhase: allocationParam.projectPhase,
    billable: allocationParam.billable,
    projectId: allocationParam.projectId,
    overAllocations: [],
    overAllocationsForUI: [],
    medium: allocationParam.medium,
    ...getSchedulerEventProps(SchedulerEventType.Allocation, {
      cls: getSchedulerEventsClass(
        SchedulerEventType.Allocation,
        `${softAllocationsEnabled ? allocationParam.medium : ''}`.toLowerCase(),
      ),
    }),
  };

  if (allocationParam.type === ResourceType.User) {
    return {
      ...commonProps,
      resourceId:
        groupBy === GroupBy.Projects
          ? resourceIdForUserAsLeaf({
              projectId: allocationParam.projectId,
              userId: allocationParam.userId,
              roleId: allocationParam.roleId,
              referenceTeamId: allocationParam.referenceTeamId,
            })
          : resourceIdForProjectAsLeaf({
              projectId: allocationParam.projectId,
              userId: allocationParam.userId,
              roleId: allocationParam.roleId,
              referenceTeamId: allocationParam.referenceTeamId,
            }),
      userId: allocationParam.userId,
      roleId: allocationParam.roleId,
      referenceTeamId: allocationParam.referenceTeamId,
    };
  }

  return {
    ...commonProps,
    resourceId: resourceIdForTeamAsLeaf(
      allocationParam.projectId,
      allocationParam.teamId,
    ),
    teamId: allocationParam.teamId,
    roleId: allocationParam.roleId,
  };
};
export const asValidAllocationObject = (data: any): Allocation => {
  if (data.userId != null) {
    return {
      ...data,
      type: ResourceType.User,
    };
  }

  return {
    ...data,
    type: ResourceType.Team,
  };
};
